/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
} from "vue";
import { useBranding, useMenu } from "@app/vue/providers/index";
import { RouterView, RouterLink, useRoute } from "vue-router";
import { ReservationPanel } from "./components/ReservationPanel";
import { useRouter } from "vue-router";
import { PublicFooterComponent } from "../../components/PublicFooterComponent";
import { fetchGalleryAssets, GalleryAsset } from "@app/server/client";
import { formatPrice, formatPhone } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { CustomerLanguageSelector } from "../../components/CustomerLanguageSelector";

export const ShopReservePage = defineComponent({
  name: "ShopReservePage",
  setup() {
    const { shop } = useMenu();
    const { t } = useLocalization();
    const route = useRoute();
    const galleryAssets = ref<GalleryAsset[]>([]);
    const { branding } = useBranding();
    const shopSlug = computed(() => shop.value.slug);

    const loadGalleryAssets = async () => {
      try {
        galleryAssets.value = await fetchGalleryAssets(shopSlug.value);
      } catch (e) {
        console.error("Failed to load gallery:", e);
      }
    };

    onMounted(() => loadGalleryAssets());

    const getSetting = (key: string): string | boolean | null => {
      const s = shop.value?.settings?.find((s: any) => s.key === key);
      return s?.value ?? null;
    };

    const googleRating = computed(() =>
      shop.value?.google_rating ? Number(shop.value.google_rating) : null,
    );
    const googleReviewCount = computed(
      () => shop.value?.google_review_count ?? null,
    );

    const hasOrdering = computed(() => {
      const ta = getSetting("take_away");
      const del = getSetting("delivery");
      const oo = getSetting("online_ordering_enabled");
      return ta === "yes" || del === "yes" || oo === true || oo === "true";
    });

    const tabs = computed(() => {
      const slug = shop.value?.slug;
      const tabs = [
        {
          name: t("customer.reserve.tab_reservations"),
          path: `/restaurant/${slug}/reserve/tables`,
        },
        {
          name: t("customer.reserve.tab_menu"),
          path: `/restaurant/${slug}/reserve/menu`,
        },
      ];
      if (getSetting("take_away") === "yes")
        tabs.push({
          name: t("customer.reserve.tab_takeaway"),
          path: `/restaurant/${slug}/reserve/order?mode=takeaway`,
        });
      if (getSetting("delivery") === "yes")
        tabs.push({
          name: t("customer.reserve.tab_delivery"),
          path: `/restaurant/${slug}/reserve/order?mode=delivery`,
        });
      tabs.push({
        name: t("customer.reserve.tab_events"),
        path: `/restaurant/${slug}/reserve/events`,
      });
      return tabs;
    });

    const isActiveTab = (tabPath: string) => {
      const url = new URL(tabPath, "http://x");
      const pathMatch = route.path.includes(url.pathname);
      // For order tabs with mode query, check the query param too
      const modeParam = url.searchParams.get("mode");
      if (modeParam && pathMatch) {
        return route.query.mode === modeParam;
      }
      // For non-order tabs, just check path doesn't match order
      if (!modeParam && route.path.includes("/reserve/order")) return false;
      return pathMatch;
    };

    const allImages = computed(() =>
      galleryAssets.value.filter((a) =>
        (a.meta?.content_type as string)?.startsWith("image/"),
      ),
    );

    const images = computed(() => allImages.value.slice(0, 3));

    const router = useRouter();

    // Gallery lightbox state
    const showGallery = ref(false);
    const galleryIndex = ref(0);

    const openGallery = (index = 0) => {
      galleryIndex.value = index;
      showGallery.value = true;
    };

    const closeGallery = () => {
      showGallery.value = false;
    };

    const nextImage = () => {
      if (galleryIndex.value < allImages.value.length - 1) galleryIndex.value++;
      else galleryIndex.value = 0;
    };

    const prevImage = () => {
      if (galleryIndex.value > 0) galleryIndex.value--;
      else galleryIndex.value = allImages.value.length - 1;
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (!showGallery.value) return;
      if (e.key === "Escape") closeGallery();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    // Touch swipe for gallery
    let touchStartX = 0;
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      if (!showGallery.value) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        if (dx > 0) prevImage();
        else nextImage();
      }
    };

    onMounted(() => window.addEventListener("keydown", handleKeydown));
    onUnmounted(() => window.removeEventListener("keydown", handleKeydown));

    return () => (
      <div class="min-h-screen bg-white">
        {/* Hero — full bleed, cinematic, with overlay */}
        <div
          class={`relative overflow-hidden ${images.value.length > 0 ? "h-[280px] md:h-[420px]" : "h-[200px] md:h-[260px]"}`}
        >
          {/* Top nav overlaid on hero */}
          <div class="absolute top-0 left-0 right-0 z-10">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
              <div>
                {branding.value?.id === "javshani" ? (
                  <RouterLink
                    to="/"
                    class="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors"
                  >
                    <span class="material-symbols-outlined text-lg">
                      arrow_back
                    </span>
                    <span class="text-xs font-medium tracking-wider uppercase">
                      {t("customer.reserve.back")}
                    </span>
                  </RouterLink>
                ) : null}
              </div>
              <CustomerLanguageSelector variant="overlay" />
            </div>
          </div>
          {images.value.length > 0 ? (
            <img
              src={images.value[0].url}
              alt={shop.value.name}
              class="w-full h-full object-cover"
            />
          ) : (
            <div
              class="w-full h-full"
              style="background: linear-gradient(160deg, #111110 0%, #1e1b16 60%, #111110 100%); box-shadow: inset 0 -60px 80px -20px rgba(180,140,80,0.08);"
            />
          )}
          {/* Gradient overlays — top for nav, bottom for text */}
          <div class="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Restaurant info overlaid on hero */}
          <div class="absolute bottom-0 left-0 right-0">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
              <div class="flex items-end justify-between">
                <div>
                  <h1
                    class="font-light text-white drop-shadow-sm"
                    style="font-size: clamp(1.75rem, 4vw, 3rem);"
                  >
                    {shop.value.name}
                  </h1>
                  <p class="mt-2 text-sm md:text-base text-white/70">
                    {shop.value.address}
                  </p>
                  {(getSetting("take_away") === "yes" ||
                    getSetting("delivery") === "yes") && (
                    <div class="flex gap-2 mt-3">
                      {getSetting("take_away") === "yes" && (
                        <span class="text-xs text-white/90 border border-white/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
                          {t("customer.reserve.tab_takeaway")}
                        </span>
                      )}
                      {getSetting("delivery") === "yes" && (
                        <span class="text-xs text-white/90 border border-white/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
                          {t("customer.reserve.tab_delivery")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div class="flex items-center gap-3">
                  {googleRating.value !== null && (
                    <div class="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5">
                      <span style="color: var(--accent);" class="text-base">
                        ★
                      </span>
                      <span class="text-white font-medium">
                        {googleRating.value.toFixed(1)}
                        {!!googleReviewCount.value &&
                          googleReviewCount.value > 0 &&
                          ` (${googleReviewCount.value})`}
                      </span>
                    </div>
                  )}
                  {allImages.value.length > 1 && (
                    <button
                      type="button"
                      onClick={() => openGallery(0)}
                      class="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 text-white hover:bg-white/25 transition-colors"
                    >
                      <span class="material-symbols-outlined text-base">
                        photo_library
                      </span>
                      <span class="text-sm font-medium">
                        {allImages.value.length}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs + Content */}
        <div class="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Tab bar */}
          <div class="sticky top-0 z-20 bg-white -mx-4 px-4 sm:-mx-6 sm:px-6">
            <nav
              class="flex gap-1 overflow-x-auto scrollbar-hide border-b border-gray-200 -mb-px"
              role="tablist"
            >
              {tabs.value.map((tab) => {
                const active = isActiveTab(tab.path);
                return (
                  <RouterLink
                    key={tab.path}
                    to={tab.path}
                    role="tab"
                    aria-selected={active}
                    class={`px-5 py-3.5 min-h-[44px] text-sm whitespace-nowrap border-b-2 tracking-wide ${
                      active
                        ? "text-gray-900 font-semibold"
                        : "border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-200"
                    }`}
                    style={{
                      ...(active ? { borderBottomColor: "var(--accent)" } : {}),
                      transition: "color 200ms ease, border-color 200ms ease",
                    }}
                  >
                    {tab.name}
                  </RouterLink>
                );
              })}
            </nav>
          </div>

          {/* Content + Sidebar */}
          <div
            class="grid grid-cols-1 lg:grid-cols-3 gap-10"
            style="padding-top: clamp(32px, 4vw, 48px); padding-bottom: clamp(48px, 6vw, 96px);"
          >
            <div class="lg:col-span-2 min-w-0" role="tabpanel">
              <div key={route.path} class="tab-content-enter">
                <RouterView />
              </div>
            </div>
            <div class="lg:col-span-1">
              <div class="lg:sticky lg:top-14">
                <ReservationPanel
                  hideEvents={
                    route.path.includes("/reserve/order") ||
                    route.path.includes("/reserve/events")
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <PublicFooterComponent />

        {/* Gallery Lightbox */}
        {showGallery.value && allImages.value.length > 0 && (
          <div
            class="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeGallery();
            }}
            onTouchstart={handleTouchStart}
            onTouchend={handleTouchEnd}
          >
            {/* Close */}
            <button
              type="button"
              onClick={closeGallery}
              class="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"
            >
              <span class="material-symbols-outlined text-3xl">close</span>
            </button>

            {/* Counter */}
            <div class="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm">
              {galleryIndex.value + 1} / {allImages.value.length}
            </div>

            {/* Prev */}
            <button
              type="button"
              onClick={prevImage}
              class="absolute left-4 text-white/50 hover:text-white transition-colors"
            >
              <span class="material-symbols-outlined text-4xl">
                chevron_left
              </span>
            </button>

            {/* Image */}
            <img
              src={allImages.value[galleryIndex.value]?.url}
              alt=""
              class="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            />

            {/* Next */}
            <button
              type="button"
              onClick={nextImage}
              class="absolute right-4 text-white/50 hover:text-white transition-colors"
            >
              <span class="material-symbols-outlined text-4xl">
                chevron_right
              </span>
            </button>
          </div>
        )}
      </div>
    );
  },
});
